/**
 * Pengaturan Kategori Aset (user request 2026-10-22).
 *
 * - Tambah kategori manual (custom) — langsung tersedia di dropdown kategori
 *   form aset, filter daftar, dan bulk move.
 * - Hapus kategori (builtin maupun custom): aset yang memakai kategori tsb
 *   TIDAK diubah/dihapus — kolom kategorinya tampil kosong ("—") sampai
 *   dipindahkan via aksi bulk di halaman Inventaris & Aset.
 * - Menambah kembali kategori dengan nama yang sama akan mengaktifkannya lagi.
 * Semua perubahan tercatat di audit trail backend.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Trash2, RefreshCw, Info } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function AssetCategorySettings() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState({ open: false, key: null, label: "", count: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets/categories");
      setCats(data || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post("/assets/categories", { label: label.trim() });
      toast.success(`Kategori "${label.trim()}" ditambahkan`);
      setLabel("");
      load();
    } catch (e2) {
      toast.error(formatDetail(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async () => {
    try {
      const { data } = await api.delete(`/assets/categories/${encodeURIComponent(confirmDel.key)}`);
      toast.success(
        data?.affected_assets > 0
          ? `Kategori dihapus — ${data.affected_assets} aset tetap ada, kolom kategorinya kini kosong`
          : "Kategori dihapus",
      );
      setConfirmDel({ open: false, key: null, label: "", count: 0 });
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 max-w-2xl" data-testid="asset-category-settings">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Package size={15} className="text-[#0B1D3A]" />
        <div>
          <h3 className="font-display text-sm uppercase tracking-wider text-[#0B1D3A]">Kategori Aset</h3>
          <p className="text-[11px] text-slate-500">Tambah kategori manual atau hapus kategori yang tidak dipakai.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} data-testid="asset-cat-reload"
          className="ml-auto h-7 px-2.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Muat Ulang
        </button>
      </div>

      <form onSubmit={add} className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Nama kategori baru, mis. Elektronik"
          data-testid="asset-cat-new-label"
          className="flex-1 h-9 border border-slate-300 px-2 text-sm"
        />
        <button type="submit" disabled={busy || !label.trim()} data-testid="asset-cat-add"
          className="h-9 px-3 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#e04a13] disabled:opacity-40 flex items-center gap-1">
          <Plus size={12} /> Tambah
        </button>
      </form>

      <div className="divide-y divide-slate-100">
        {loading && !cats.length && (
          <div className="px-4 py-6 text-center text-slate-400 text-sm italic">Memuat…</div>
        )}
        {cats.map((c) => (
          <div key={c.key} className="px-4 py-2.5 flex items-center gap-3" data-testid={`asset-cat-row-${c.key}`}>
            <span className="font-medium text-sm text-[#0B1D3A]">{c.label}</span>
            <code className="text-[10px] text-slate-400">{c.key}</code>
            {c.builtin
              ? <span className="text-[9px] uppercase tracking-wider font-semibold text-slate-500 border border-slate-300 px-1 py-0.5">Bawaan</span>
              : <span className="text-[9px] uppercase tracking-wider font-semibold text-indigo-700 border border-indigo-300 px-1 py-0.5">Custom</span>}
            <span className="text-[10px] text-slate-500 tabular-nums">{c.asset_count ?? 0} aset</span>
            <button type="button" data-testid={`asset-cat-del-${c.key}`}
              onClick={() => setConfirmDel({ open: true, key: c.key, label: c.label, count: c.asset_count || 0 })}
              className="ml-auto p-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50" title="Hapus kategori">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex items-start gap-1.5">
        <Info size={11} className="mt-0.5 shrink-0" />
        Menghapus kategori tidak menghapus aset. Aset dengan kategori tersebut tetap ada,
        namun kolom kategorinya kosong sampai Anda memindahkannya (bisa sekaligus banyak
        lewat checkbox di halaman Inventaris &amp; Aset → "Pindah ke Kategori").
      </div>

      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(v) => setConfirmDel((s) => ({ ...s, open: v }))}
        title={`Hapus kategori "${confirmDel.label}"?`}
        description={confirmDel.count > 0
          ? `${confirmDel.count} aset memakai kategori ini. Aset TIDAK dihapus — kolom kategorinya akan kosong sampai dipindahkan.`
          : "Kategori ini tidak dipakai aset manapun."}
        confirmLabel="Hapus Kategori"
        onConfirm={del}
      />
    </div>
  );
}
